import {
  createOfferComment,
  createOfferCommentWithSignature,
  deleteOfferComment,
  getNotificationsInbox,
  getOfferComments,
  getOfferCommentsWithSignature,
  getUnreadCount,
  markCommentsAsRead,
  resolveOfferComment,
} from '@lib/api';
import { toProblem } from '@lib/api/base';
import type { CreateCommentData, NotificationsInbox, OfferComment, Problem } from '@lib/api/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@ui/use-toast';

/**
 * Hook for fetching sticky note comments on an offer (authenticated recruiter)
 */
export const useOfferComments = (offerId: string | undefined) => {
  return useQuery<OfferComment[], Problem>({
    queryKey: ['offer-comments', offerId],
    queryFn: () => {
      if (!offerId) throw new Error('Offer ID is required');
      return getOfferComments(offerId);
    },
    enabled: !!offerId,
    staleTime: 30 * 1000, // 30 seconds - comments should be relatively fresh
  });
};

/**
 * Hook for fetching sticky note comments on an offer using a signed URL (candidate)
 */
export const useOfferCommentsWithSignature = (signedData: string | undefined) => {
  return useQuery<OfferComment[], Problem>({
    queryKey: ['offer-comments', 'signature', signedData],
    queryFn: () => {
      if (!signedData) throw new Error('Signed data is required');
      return getOfferCommentsWithSignature(signedData);
    },
    enabled: !!signedData,
    staleTime: 30 * 1000, // 30 seconds - comments should be relatively fresh
  });
};

/**
 * Hook for creating a sticky note comment on an offer (authenticated recruiter)
 */
export const useCreateOfferComment = (offerId: string) => {
  const queryClient = useQueryClient();

  return useMutation<OfferComment, Problem, CreateCommentData>({
    mutationFn: data => createOfferComment(offerId, data),
    onSuccess: () => {
      // Invalidate the comments query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['offer-comments', offerId] });
      // Invalidate notifications inbox as we've interacted with comments
      queryClient.invalidateQueries({ queryKey: ['notifications-inbox'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });

      toast({
        title: 'Comment added',
        description: 'Your comment has been posted successfully.',
      });
    },
    onError: error => {
      const problem = toProblem(error);
      console.error('Error creating comment:', error);
      toast({
        title: 'Error',
        description: problem.detail || 'Failed to post comment. Please try again.',
        variant: 'destructive',
      });
    },
  });
};

/**
 * Hook for creating a sticky note comment on an offer using a signed URL (candidate)
 */
export const useCreateOfferCommentWithSignature = (signedData: string) => {
  const queryClient = useQueryClient();

  return useMutation<OfferComment, Problem, CreateCommentData>({
    mutationFn: data => createOfferCommentWithSignature(signedData, data),
    onSuccess: () => {
      // Invalidate the comments query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['offer-comments', 'signature', signedData] });

      toast({
        title: 'Message sent',
        description: 'Your message has been sent to the recruiter.',
      });
    },
    onError: error => {
      const problem = toProblem(error);
      console.error('Error creating comment:', error);
      toast({
        title: 'Error',
        description: problem.detail || 'Failed to send message. Please try again.',
        variant: 'destructive',
      });
    },
  });
};

/**
 * Hook for deleting a comment on an offer (authenticated recruiter only)
 */
export const useDeleteOfferComment = (offerId: string) => {
  const queryClient = useQueryClient();

  return useMutation<{ id: string }, Problem, string>({
    mutationFn: commentId => deleteOfferComment(offerId, commentId),
    onSuccess: () => {
      // Invalidate the comments query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['offer-comments', offerId] });

      toast({
        title: 'Comment deleted',
        description: 'The comment has been removed.',
      });
    },
    onError: error => {
      const problem = toProblem(error);
      console.error('Error deleting comment:', error);
      toast({
        title: 'Error',
        description: problem.detail || 'Failed to delete comment. Please try again.',
        variant: 'destructive',
      });
    },
  });
};

/**
 * Hook for resolving a comment thread (authenticated recruiter only)
 */
export const useResolveOfferComment = (offerId: string) => {
  const queryClient = useQueryClient();

  return useMutation<OfferComment, Problem, string>({
    mutationFn: commentId => resolveOfferComment(offerId, commentId),
    onSuccess: () => {
      // Invalidate the comments query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['offer-comments', offerId] });

      toast({
        title: 'Thread resolved',
        description: 'The comment thread has been marked as resolved.',
      });
    },
    onError: error => {
      const problem = toProblem(error);
      console.error('Error resolving comment:', error);
      toast({
        title: 'Error',
        description: problem.detail || 'Failed to resolve comment. Please try again.',
        variant: 'destructive',
      });
    },
  });
};

/**
 * Combined hook that provides the appropriate comments functionality
 * based on whether it's a public (candidate) or authenticated (recruiter) view
 */
export const useOfferCommentsManagement = (
  offerId: string | undefined,
  signedData: string | undefined
) => {
  const isPublicView = Boolean(signedData);

  // Use the appropriate query hook based on view mode
  const authenticatedQuery = useOfferComments(isPublicView ? undefined : offerId);
  const publicQuery = useOfferCommentsWithSignature(isPublicView ? signedData : undefined);

  // Combine the results based on mode
  const {
    data: comments,
    isLoading,
    error,
    refetch,
  } = isPublicView ? publicQuery : authenticatedQuery;

  return {
    comments: comments ?? [],
    isLoading,
    error,
    refetch,
    isPublicView,
  };
};

// Notifications Inbox Hooks

/**
 * Hook for fetching the notifications inbox
 */
export const useNotificationsInbox = () => {
  return useQuery<NotificationsInbox, Problem>({
    queryKey: ['notifications-inbox'],
    queryFn: getNotificationsInbox,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Refetch every minute for near-real-time updates
  });
};

/**
 * Hook for fetching the unread count (lightweight)
 */
export const useUnreadCount = () => {
  return useQuery<{ total_unread: number }, Problem>({
    queryKey: ['notifications-unread-count'],
    queryFn: getUnreadCount,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 30 * 1000, // Refetch every 30 seconds for the badge
  });
};

/**
 * Hook for marking comments as read
 */
export const useMarkCommentsAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation<{ marked_count: number }, Problem, string[]>({
    mutationFn: commentIds => markCommentsAsRead(commentIds),
    onSuccess: () => {
      // Invalidate notifications queries to update counts
      queryClient.invalidateQueries({ queryKey: ['notifications-inbox'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
    onError: error => {
      console.error('Error marking comments as read:', error);
    },
  });
};
